import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Row, Col, Typography, InputNumber, Input, Select, Button, Space, Divider, Tag, Table } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import apiService from '../services/api'
import './NotaManutencaoModal.css'
import { isAndroid, isAndroidFunctionAvailable } from '../utils/androidUtils'

const { Title, Text } = Typography

const motivosVolumes = [
  { value: '1', label: 'Carta de Correção' },
  { value: '2', label: 'Unitização' }
]

const motivosCep = [
  { value: '1', label: 'Local de Entrega na NF' },
  { value: '2', label: 'Redespacho' },
  { value: '3', label: 'Carta de correção' }
]

function NotaManutencaoModal({
  open,
  onClose,
  chave,
  nota,
  pedido,
  volumesAntes,
  cepAntes,
  enderecoAntes,
  nroAntes,
  cnpjRemetente,
  newCepInfo,
  onBuscarCep,
  onAlterarVolumes,
  onAlterarCep
}) {
  const [volumesDepois, setVolumesDepois] = useState(undefined)
  const [motivoVolumes, setMotivoVolumes] = useState(undefined)
  const [cepDepois, setCepDepois] = useState('')
  const [motivoCep, setMotivoCep] = useState(undefined)
  const [cepInfoLocal, setCepInfoLocal] = useState(newCepInfo || {})
  const [loadingBuscaCep, setLoadingBuscaCep] = useState(false)
  const [cubagemItems, setCubagemItems] = useState([])
  const [endereco, setEndereco] = useState('')
  const [nro, setNro] = useState('')

  const isAndroidDevice = isAndroid()

  const isCepIncompleto = useMemo(() => {
    return cepAntes && String(cepAntes).endsWith('000')
  }, [cepAntes])

  const startMeasurement = () => {
    if (isAndroidFunctionAvailable('measureVolume')) {
      window.Android.measureVolume()
    }
  }

  useEffect(() => {
    if (open) {
      setEndereco(enderecoAntes || '')
      setNro(nroAntes || '')
    }
  }, [open, enderecoAntes, nroAntes])

  useEffect(() => {
    if (isAndroidDevice) {
      window.cubagem = (comprimento, largura, altura, volume) => {
        alert(`Volume calculado: ${volume} m³`)
        const newItem = {
          key: Date.now(),
          comprimento: Number(comprimento),
          largura: Number(largura),
          altura: Number(altura),
          volume: Number(volume)
        }
        setCubagemItems(prev => [...prev, newItem])
      }
    }

    return () => {
      if (typeof window !== 'undefined' && window.cubagem) {
        delete window.cubagem
      }
    }
  }, [isAndroidDevice])

  const columnsCubagem = [
    { title: 'Compr. (m)', dataIndex: 'comprimento', key: 'comprimento', render: (v) => v?.toFixed(2) },
    { title: 'Larg. (m)', dataIndex: 'largura', key: 'largura', render: (v) => v?.toFixed(2) },
    { title: 'Alt. (m)', dataIndex: 'altura', key: 'altura', render: (v) => v?.toFixed(2) },
    { title: 'Vol. (m³)', dataIndex: 'volume', key: 'volume', render: (v) => v?.toFixed(3) }
  ]

  useEffect(() => {
    setCepInfoLocal(newCepInfo || {})
  }, [newCepInfo])

  const canAlterarVolumes = useMemo(() => {
    return typeof volumesDepois === 'number' && volumesDepois >= 1 && volumesDepois <= 9999 && !!motivoVolumes
  }, [volumesDepois, motivoVolumes])

  const canAlterarCep = useMemo(() => {
    return cepDepois && cepDepois.length === 8 && !!motivoCep && !!nro
  }, [cepDepois, motivoCep, nro])

  const handleBuscarCep = async () => {
    if (!onBuscarCep) return
    if (!cepDepois || cepDepois.length !== 8) return
    setLoadingBuscaCep(true)
    try {
      const info = await onBuscarCep(cepDepois)
      if (info) {
        setCepInfoLocal(info)

        // Popular logradouro e número se disponíveis
        if (info.logradouro || info.endereco) {
          setEndereco(info.logradouro || info.endereco)
        }
        if (info.nro) {
          setNro(info.nro)
        }
      }
    } finally {
      setLoadingBuscaCep(false)
    }
  }

  const handleAlterarVolumes = async () => {
    if (!onAlterarVolumes) return
    if (!canAlterarVolumes) return
    const label = motivosVolumes.find(m => m.value === motivoVolumes)?.label || motivoVolumes
    await onAlterarVolumes(chave, volumesDepois, label)
  }

  const handleAlterarCep = async () => {
    if (!onAlterarCep) return
    if (!canAlterarCep) return
    const label = motivosCep.find(m => m.value === motivoCep)?.label || motivoCep
    await onAlterarCep(chave, cepDepois, label, { ...cepInfoLocal, logradouro: endereco, nro })
  }

  const setCepDigits = (v) => {
    const digits = String(v || '').replace(/\D/g, '').slice(0, 8)
    setCepDepois(digits)
  }

  return (
    <Modal
      open={open}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: '32px' }}>
          <span>Manutenção</span>
          <Button
            type="primary"
            style={{
              backgroundColor: isAndroidDevice ? '#52c41a' : '#ff4d4f',
              borderColor: isAndroidDevice ? '#52c41a' : '#ff4d4f'
            }}
            onClick={startMeasurement}
            disabled={!isAndroidDevice}
          >
            Medir Cubagem
          </Button>
        </div>
      }
      onCancel={onClose}
      footer={null}
      maskClosable={false}
      centered
      wrapClassName="nota-manutencao-modal"
    >
      <div className="nm-content-container" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Divider orientation="left">Dados do remetente</Divider>
        <Row gutter={8} style={{ marginBottom: 8 }}>
          <Col span={24}>
            <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>CNPJ</label>
            <Input
              value={cnpjRemetente}
              readOnly
              disabled
              style={{ backgroundColor: '#f5f5f5' }}
            />
          </Col>
        </Row>

        <Row gutter={8} style={{ marginBottom: 8 }} className="header-row">
          <Col span={12}>
            <Text style={{ fontSize: 12 }}>NOTA: <strong>{nota}</strong></Text>
          </Col>
          <Col span={12}>
            <Text style={{ fontSize: 12 }}>PEDIDO: <strong>{pedido}</strong></Text>
          </Col>
        </Row>

        <Divider orientation="left">Volumes</Divider>
        <Row gutter={8} style={{ marginBottom: 8 }}>
          <Col span={12}>
            <Text style={{ fontSize: 12 }}>ANTES: <strong>{volumesAntes}</strong></Text>
          </Col>
          <Col span={12}>
            <Space align="center">
              <Text style={{ fontSize: 12 }}>DEPOIS:</Text>
              <InputNumber min={1} max={9999} value={volumesDepois} onChange={setVolumesDepois} />
            </Space>
          </Col>
        </Row>
        <Row style={{ marginBottom: 8 }}>
          <Col span={24}>
            <Select
              placeholder="MOTIVO"
              value={motivoVolumes}
              onChange={setMotivoVolumes}
              style={{ width: '100%' }}
              options={[{ value: '', label: '' }, ...motivosVolumes]}
            />
          </Col>
        </Row>
        <Row justify="end" style={{ marginBottom: 16 }}>
          <Col>
            <Button type="primary" onClick={handleAlterarVolumes} disabled={!canAlterarVolumes}>Alterar</Button>
          </Col>
        </Row>

        {cubagemItems.length > 0 && (
          <>
            <Divider orientation="left">Cubagem (Android)</Divider>
            <Table
              dataSource={cubagemItems}
              columns={columnsCubagem}
              size="small"
              pagination={false}
              style={{ marginBottom: 16 }}
            />
          </>
        )}

        <Divider orientation="left">CEP de Entrega</Divider>
        <Row gutter={1} style={{ marginBottom: 8 }}>
          <Col span={6}>
            <Text style={{ fontSize: 12 }}>ANTES:</Text>
          </Col>
          <Col span={18}>
            <Space>
              <Text style={{ fontSize: 12 }}>DEPOIS:</Text>
            </Space>
          </Col>
        </Row>
        <Row gutter={1} style={{ marginBottom: 8 }}>
          <Col span={6}>
            <strong>{cepAntes}</strong>
          </Col>
          <Col span={18}>
            <Space>
              <Input value={cepDepois} onChange={(e) => setCepDigits(e.target.value)} maxLength={8} className="cep-input" />
              <Button icon={<SearchOutlined />} onClick={handleBuscarCep} loading={loadingBuscaCep}>Buscar</Button>
            </Space>
          </Col>
        </Row>

        <Divider orientation="left">Endereço de Entrega</Divider>
        <Row gutter={8} style={{ marginBottom: 8 }}>
          <Col span={18}>
            <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Logradouro</label>
            <Input
              placeholder="Endereço"
              value={endereco}
              onChange={(e) => setEndereco(e.target.value)}
              style={isCepIncompleto ? { borderColor: 'red' } : {}}
            />
          </Col>
          <Col span={6}>
            <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Número</label>
            <Input
              placeholder="Nº"
              value={nro}
              onChange={(e) => setNro(e.target.value)}
              style={(!nro || isCepIncompleto) ? { borderColor: 'red' } : {}}
            />
          </Col>
        </Row>

        <Divider orientation="left">Alterar para: <Text strong>{cepInfoLocal?.cep || ''}</Text></Divider>
        <Row gutter={8} style={{ marginBottom: 4 }}>
          <Col span={16}><Text style={{ fontSize: 12 }}>Cidade</Text></Col>
          <Col span={4} style={{ textAlign: 'center' }}><Text style={{ fontSize: 12 }}>Praça</Text></Col>
          <Col span={4} style={{ textAlign: 'center' }}><Text style={{ fontSize: 12 }}>C.D</Text></Col>
        </Row>
        <Row gutter={8} style={{ marginBottom: 12 }}>
          <Col span={16}>
            <Space>
              <Text strong>{cepInfoLocal?.cidade || ''}</Text>
              <Text strong>-</Text>
              <Text strong>{cepInfoLocal?.uf || ''}</Text>
            </Space>
          </Col>
          <Col span={4} style={{ textAlign: 'center' }}>
            <Tag color="blue">{cepInfoLocal?.praca || ''}</Tag>
          </Col>
          <Col span={4} style={{ textAlign: 'center' }}>
            <Tag>{cepInfoLocal?.cd || ''}</Tag>
          </Col>
        </Row>

        <Row style={{ marginBottom: 8 }}>
          <Col span={24}>
            <Select
              placeholder="MOTIVO"
              value={motivoCep}
              onChange={setMotivoCep}
              style={{ width: '100%' }}
              options={[{ value: '', label: '' }, ...motivosCep]}
            />
          </Col>
        </Row>
        <Row justify="end">
          <Col>
            <Button type="primary" onClick={handleAlterarCep} disabled={!canAlterarCep}>Alterar</Button>
          </Col>
        </Row>
      </div>
    </Modal>
  )
}

export default NotaManutencaoModal